/**
 * Aba Checklist em leitura (PR2).
 *
 * `checklistTemplateId` (string | null) é a única fonte no Playbook. Quando
 * vinculada E o usuário tem `checklists-basic`, resolve nome + itens via
 * GET /v1/checklist-templates/:id (query gateada no `enabled` — não vaza 403).
 *
 * Honest-defer em camadas:
 *   - sem vínculo (null)        → "Nenhuma checklist vinculada"
 *   - vínculo mas sem permissão → mostra o id, sem fabricar itens
 *   - vínculo + permissão       → nome + itens (crítico/normal, ordenados)
 *
 * Comportamento de não-cumprimento do protótipo (block-close/warn) não tem
 * fonte no Playbook → omitido. Edição chega no PR4.
 */

import { ChecklistPriorityBadge } from '../PlaybookBadges';
import { useChecklistTemplate } from '../use-playbooks';

import type { PlaybookDto, ChecklistTemplateItemDto } from '../types';

export function TabChecklist({ playbook }: { playbook: PlaybookDto }): JSX.Element {
  const templateId = playbook.checklistTemplateId;
  const { data, isLoading, isError, canRead } = useChecklistTemplate(templateId);

  return (
    <div className="pb-panel" role="tabpanel" aria-label="Checklist">
      <h2 className="pb-panel__title">Checklist</h2>

      {templateId === null ? (
        <p className="pb-read-value pb-read-value--muted">Nenhuma checklist vinculada</p>
      ) : (
        <>
          <div className="pb-field">
            <span className="pb-field__label">Checklist vinculada</span>
            {data !== undefined ? (
              <p className="pb-read-value">
                {data.name} <span className="pb-read-value--muted">(v{data.version})</span>
              </p>
            ) : (
              <p className="pb-read-value pb-read-value--mono">{templateId}</p>
            )}
          </div>

          {!canRead && (
            <p className="pb-read-value pb-read-value--muted">
              Os itens desta checklist requerem a capability checklists-basic.
            </p>
          )}
          {canRead && isLoading && <p className="pb-panel__subtitle">Carregando checklist…</p>}
          {canRead && isError && (
            <p className="pb-read-value pb-read-value--muted">
              Não foi possível carregar os itens desta checklist.
            </p>
          )}

          {data !== undefined && (
            <div className="pb-panel__section">
              <span className="pb-panel__section-label">Itens</span>
              {data.items.length > 0 ? (
                <div className="pb-ci-list">
                  {[...data.items]
                    .sort((a, b) => a.order - b.order)
                    .map((item: ChecklistTemplateItemDto, i) => (
                      <div key={item.templateItemId} className="pb-ci-row">
                        <span className="pb-ci-row__num">#{i + 1}</span>
                        <div className="pb-ci-row__body">
                          <span className="pb-ci-row__label">{item.name}</span>
                          {item.description !== undefined && item.description !== '' && (
                            <span className="pb-read-value pb-read-value--muted">
                              {item.description}
                            </span>
                          )}
                        </div>
                        <ChecklistPriorityBadge isCritical={item.isCritical} />
                      </div>
                    ))}
                </div>
              ) : (
                <p className="pb-read-value pb-read-value--muted">Esta checklist não tem itens.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
